import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoContactRequest, IOpoContactsData } from '@nkr/models/opo-contact';
import { IOpoSaveResult } from '@nkr/models/opo-document';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

interface IRetVal {
    data: IOpoContactsData | null,
    loading: boolean,
    error: string,
    save: (contactId: number | null, request: IOpoContactRequest) => Promise<IOpoSaveResult>,
    remove: (contactId: number) => Promise<IOpoSaveResult>
}

const useOpoContacts = (): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [data, setData] = useState<IOpoContactsData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const baseUrl = `${__API__}/opo/${opoId}/contacts`;

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync('Getting contacts, one moment please...'));

            try {
                const response: ITypedBaseResponse<IOpoContactsData> = await dispatch(AuthActionCreators.apiGet({ url: baseUrl }));

                if (!isCurrent) {
                    return;
                }

                if (response && response.data.success) {
                    setData(response.data.results);
                } else {
                    setError('Unable to load contacts');
                }
            } catch (err) {
                if (isCurrent) {
                    setError('Unable to load contacts');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId, refreshKey]);

    const runAction = async (message: string, action: () => Promise<ITypedBaseResponse<any>>, failureMessage: string): Promise<IOpoSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync(message));

        try {
            const response = await action();

            if (response && response.data.success) {
                setRefreshKey(x => x + 1);
                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || failureMessage };
        } catch (err) {
            return { success: false, message: failureMessage };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const save = (contactId: number | null, request: IOpoContactRequest) => runAction(
        'Saving contact, one moment please...',
        () => dispatch(contactId
            ? AuthActionCreators.apiPut({ url: `${baseUrl}/${contactId}`, data: request })
            : AuthActionCreators.apiPost({ url: baseUrl, data: request })),
        'Unable to save the contact');

    const remove = (contactId: number) => runAction(
        'Deleting contact, one moment please...',
        () => dispatch(AuthActionCreators.apiDelete({ url: `${baseUrl}/${contactId}` })),
        'Unable to delete the contact');

    return { data, loading, error, save, remove };
}

export default useOpoContacts;
